import { useState, useRef } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { uploadData, construct, clearGraph } from '@/lib/api/sparql-adapter'
import { useConnectionStore } from '@/stores/connectionStore'
import { useQueryClient } from '@tanstack/react-query'
import { Upload, Download, FileText, Loader2, CheckCircle, AlertCircle, X, RefreshCw } from 'lucide-react'

interface ImportExportDialogProps {
  mode: 'import' | 'export'
  onClose: () => void
}

type RdfFormat = 'text/turtle' | 'application/ld+json' | 'application/rdf+xml'

const FORMAT_OPTIONS: { value: RdfFormat; label: string; extension: string }[] = [
  { value: 'text/turtle', label: 'Turtle (.ttl)', extension: 'ttl' },
  { value: 'application/ld+json', label: 'JSON-LD (.jsonld)', extension: 'jsonld' },
  { value: 'application/rdf+xml', label: 'RDF/XML (.rdf)', extension: 'rdf' },
]

export function ImportExportDialog({ mode, onClose }: ImportExportDialogProps) {
  const { isConnected } = useConnectionStore()
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [format, setFormat] = useState<RdfFormat>('text/turtle')
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null)
  const [fileContent, setFileContent] = useState<string>('')
  const [fileName, setFileName] = useState<string>('')
  const [replaceData, setReplaceData] = useState(true)

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setFileName(file.name)
    const reader = new FileReader()
    reader.onload = (event) => {
      setFileContent(event.target?.result as string)
    }
    reader.readAsText(file)

    // Auto-detect format from extension
    if (file.name.endsWith('.ttl')) setFormat('text/turtle')
    else if (file.name.endsWith('.jsonld') || file.name.endsWith('.json')) setFormat('application/ld+json')
    else if (file.name.endsWith('.rdf') || file.name.endsWith('.xml')) setFormat('application/rdf+xml')
  }

  const handleImport = async () => {
    if (!fileContent) return

    setLoading(true)
    setResult(null)

    try {
      // Clear existing data first if replaceData is enabled
      if (replaceData) {
        await clearGraph()
      }

      await uploadData(fileContent, format)

      // Invalidate all queries to refresh the UI
      queryClient.invalidateQueries()

      setResult({
        success: true,
        message: replaceData
          ? 'Data replaced successfully!'
          : 'Data imported successfully!'
      })
    } catch (err) {
      setResult({
        success: false,
        message: err instanceof Error ? err.message : 'Import failed',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleExport = async () => {
    setLoading(true)
    setResult(null)

    try {
      // Use CONSTRUCT to get all triples
      const data = await construct(`
        CONSTRUCT { ?s ?p ?o }
        WHERE { ?s ?p ?o }
      `)

      // Create download
      const formatInfo = FORMAT_OPTIONS.find((f) => f.value === format)!
      const blob = new Blob([data], { type: format })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `intellispot-export.${formatInfo.extension}`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)

      setResult({ success: true, message: 'Data exported successfully!' })
    } catch (err) {
      setResult({
        success: false,
        message: err instanceof Error ? err.message : 'Export failed',
      })
    } finally {
      setLoading(false)
    }
  }

  if (!isConnected) {
    return (
      <Card className="fixed inset-0 z-50 m-auto w-[400px] h-fit">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            {mode === 'import' ? 'Import Data' : 'Export Data'}
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-text-muted text-sm">
            Please connect to a Fuseki endpoint first.
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <Card className="w-[500px]" onClick={(e) => e.stopPropagation()}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              {mode === 'import' ? (
                <>
                  <Upload className="w-5 h-5" />
                  Import RDF Data
                </>
              ) : (
                <>
                  <Download className="w-5 h-5" />
                  Export RDF Data
                </>
              )}
            </CardTitle>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
          <CardDescription>
            {mode === 'import'
              ? 'Upload RDF data to the triplestore'
              : 'Download the current graph as RDF'}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Format Selection */}
          <div>
            <label className="text-sm font-medium text-text mb-2 block">Format</label>
            <div className="flex gap-2">
              {FORMAT_OPTIONS.map((opt) => (
                <Button
                  key={opt.value}
                  variant={format === opt.value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFormat(opt.value)}
                >
                  {opt.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Import File Selection */}
          {mode === 'import' && (
            <div>
              <label className="text-sm font-medium text-text mb-2 block">File</label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".ttl,.jsonld,.json,.rdf,.xml"
                onChange={handleFileSelect}
                className="hidden"
              />
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-border rounded-lg p-8 text-center cursor-pointer hover:border-primary transition-colors"
              >
                {fileName ? (
                  <div className="flex items-center justify-center gap-2">
                    <FileText className="w-5 h-5 text-primary" />
                    <span className="text-sm font-medium">{fileName}</span>
                    <Badge variant="outline">{(fileContent.length / 1024).toFixed(1)} KB</Badge>
                  </div>
                ) : (
                  <>
                    <Upload className="w-8 h-8 mx-auto text-text-muted mb-2" />
                    <p className="text-sm text-text-muted">
                      Click to select a file or drag and drop
                    </p>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Preview for import */}
          {mode === 'import' && fileContent && (
            <div>
              <label className="text-sm font-medium text-text mb-2 block">Preview</label>
              <pre className="bg-background rounded-lg p-3 text-xs font-mono max-h-32 overflow-auto">
                {fileContent.slice(0, 500)}
                {fileContent.length > 500 && '...'}
              </pre>
            </div>
          )}

          {/* Replace Data Option */}
          {mode === 'import' && (
            <div className="flex items-center gap-3 p-3 bg-warm-50 rounded-lg border border-border-subtle">
              <input
                type="checkbox"
                id="replaceData"
                checked={replaceData}
                onChange={(e) => setReplaceData(e.target.checked)}
                className="w-4 h-4 rounded border-warm-400 text-warm-800 focus:ring-warm-400 focus:ring-offset-0"
              />
              <label htmlFor="replaceData" className="flex-1">
                <span className="text-sm font-medium text-text flex items-center gap-2">
                  <RefreshCw className="w-4 h-4" />
                  Replace existing data
                </span>
                <p className="text-xs text-text-muted mt-0.5">
                  Clear all existing triples before importing new data
                </p>
              </label>
            </div>
          )}

          {/* Result Message */}
          {result && (
            <div
              className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm ${
                result.success
                  ? 'bg-business/10 text-business'
                  : 'bg-solution/10 text-solution'
              }`}
            >
              {result.success ? (
                <CheckCircle className="w-4 h-4" />
              ) : (
                <AlertCircle className="w-4 h-4" />
              )}
              {result.message}
            </div>
          )}

          {/* Action Button */}
          <Button
            className="w-full"
            onClick={mode === 'import' ? handleImport : handleExport}
            disabled={loading || (mode === 'import' && !fileContent)}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                {mode === 'import' ? 'Importing...' : 'Exporting...'}
              </>
            ) : mode === 'import' ? (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Import Data
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Export Data
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}
